import * as React from 'react'
import * as R from 'ramda'
import { connect, FormikContext, FormikProps, getIn, setIn } from 'formik'

import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import {
  ValidationError,
  ValidatorInfo
} from '@espoon-voltti/ts-libs/lib/validation'

import { FieldState } from '@espoon-voltti/react-components/elements/form/types'

export const fieldState = (
  fieldName: string,
  formik: FormikProps<any>,
  touched: boolean = false,
  useSuccess: boolean = false
): FieldState | undefined => {
  const touch = touched || getIn(formik.touched, fieldName)
  const error = getIn(formik.errors, fieldName)
  let state: O.Option<FieldState> = O.none

  if (touch) {
    if (error) {
      state = O.some<FieldState>('warning')
    }

    if (useSuccess) {
      state = O.some<FieldState>('success')
    }
  }

  return O.toUndefined(state)
}

export function maybeFormikErrors<T>(
  validation: E.Either<ValidationError[], T>
): O.Option<Record<string, any>> {
  return pipe(
    validation,
    E.fold(
      (l) => {
        // for each unique field, select first validation error
        const firstErrors = R.values(R.groupBy((x) => x.path.join('.'), l)).map(
          (xs) => xs[0]
        )
        // convert to object based on paths, this is what formik expects
        return O.some(
          firstErrors.reduce((acc, x) => setIn(acc, x.path.join('.'), x), {})
        )
      },
      () => O.none
    )
  )
}

type Translator = <T extends ValidatorInfo>(x: T) => React.ReactNode
export const ErrorMessageCtx = React.createContext<Translator | null>(null)
export const useErrorMessages = () => {
  const ctx = React.useContext(ErrorMessageCtx)
  if (!ctx) {
    throw new Error('Must be used within <ErrorMessageCtx.Provider>')
  }
  return ctx
}

export interface ErrorMessageProps {
  name: string
  touched?: boolean
}
const ErrorMessageImpl: React.FC<
  ErrorMessageProps & { formik: FormikContext<any> }
> = ({ name, formik, touched }) => {
  const touch = touched || getIn(formik.touched, name)
  const error: ValidationError | undefined = getIn(formik.errors, name)
  const translator = useErrorMessages()

  return touch && error ? <span>{translator(error.info)}</span> : null
}

export const ErrorMessage = connect<
  ErrorMessageProps,
  ErrorMessageProps & { formik: FormikContext<any> }
>(ErrorMessageImpl)

export const setFieldValue =
  (form: FormikProps<any>, name: string) => (value: unknown) => {
    form.setFieldValue(name, value)
  }

export const setFieldTouched = (form: FormikProps<any>, name: string) => () =>
  form.setFieldTouched(name, true)
